import { computed, type ComputedRef } from 'vue'

import type { ProductInsightCounts } from '../models/productInsights.types'
import {
  countDemandIncreased,
  countIssueTypes,
  countProductsWithIssues,
} from '../service/ecommerce/counts'

import {
  useReportData,
  useReportSchedules,
  type ReportSpec,
  type ScheduleTab,
  type ScheduleTabSelection,
} from '@/features/reports'

// The two report reads behind the Product Insights KPI cards (ORP-18953). The approved
// follow-up intentionally omits the legacy Price Sensitive `overview`/`total_rows` probe until
// backend verifies its semantics. A missing product-bestsellers partition likewise contributes
// 0 without issuing a category-level fallback request (spec stays null → idle).

const REPORT_ID = 17
const ISSUES_TAB = 'merchantcenter_product_issues'
const PRODUCT_BEST_SELLERS_TAB = 'product_bestsellers'
/** Product-approved issue-type filter: any value containing a Latin letter counts as an issue. */
const ISSUE_TYPE_REGEX = '.*[A-Za-z].*'

export type ProductInsightCountsState = 'loading' | 'error' | 'ready'

export interface UseProductInsightCounts {
  counts: ComputedRef<ProductInsightCounts>
  state: ComputedRef<ProductInsightCountsState>
  reload: () => Promise<void>
}

function firstTabDetail(selection: ScheduleTabSelection): ScheduleTab | null {
  const first = Array.isArray(selection) ? selection[0] : selection
  return typeof first === 'object' && first !== null ? first : null
}

export function useProductInsightCounts(): UseProductInsightCounts {
  const schedules = useReportSchedules()

  const issuesSpec = computed<ReportSpec | null>(() => {
    const tab = firstTabDetail(schedules.checkScheduleTabs(REPORT_ID, ISSUES_TAB))
    if (!tab?.partition_name) return null
    return {
      report_id: REPORT_ID,
      dimensions: ['product_id', 'product_issue_type'],
      filters: [{ field: 'product_issue_type', match_mode: 'regex', value: ISSUE_TYPE_REGEX }],
      pagination_enabled: false,
      partition_name: tab.partition_name,
    }
  })

  const bestSellersSpec = computed<ReportSpec | null>(() => {
    const tab = firstTabDetail(schedules.checkScheduleTabs(REPORT_ID, PRODUCT_BEST_SELLERS_TAB))
    if (!tab?.partition_name) return null
    return {
      report_id: REPORT_ID,
      dimensions: ['product_name', 'relative_demand_change'],
      pagination_enabled: false,
      partition_name: tab.partition_name,
    }
  })

  const issues = useReportData(issuesSpec)
  const bestSellers = useReportData(bestSellersSpec)
  const reads = [issues, bestSellers]

  const counts = computed<ProductInsightCounts>(() => ({
    productIssues: countProductsWithIssues(issues.rows.value),
    productIssueTypes: countIssueTypes(issues.rows.value),
    demandIncreasedProducts: countDemandIncreased(bestSellers.rows.value),
  }))

  const state = computed<ProductInsightCountsState>(() => {
    if (reads.some((read) => read.state.value === 'loading')) return 'loading'
    if (reads.some((read) => read.state.value === 'error')) return 'error'
    return 'ready'
  })

  const reload = async (): Promise<void> => {
    await Promise.all(reads.map((read) => read.reload()))
  }

  return { counts, state, reload }
}
